import type { Expression } from "../expressions/index.ts";
import { isWord, Match, Op } from "../expressions/index.ts";
import type { ByteReader } from "./ByteReader.ts";
import { ByteWriter } from "./ByteWriter.ts";
import type { IParseOutput } from "./textFormat.ts";
import { Token } from "./textFormat.ts";

/* eslint-disable @typescript-eslint/no-non-null-assertion */

export enum StoredOp {
  Match = 0, // Op.Match,
  Or = 1, // Op.Or,
  And = 2, // Op.And,

  /// Preserves ending whitespace
  End = 28,
  /// Force parentheses, preserve parenthesis whitespace.
  Paren = 29,
  /// Raw expression text operator, for preserving unparsable input.
  Raw = 30,
  /// Reserved for extended operators, when we run out of ops.
  Extended = 31,
}

export type StoredExpression =
  | { op: StoredOp.End; inner: StoredExpression; trail?: string }
  | { op: StoredOp.Paren; inner: StoredExpression; openLead?: string; closeLead?: string }
  | { op: StoredOp.Raw; source: string }
  | { op: StoredOp.Match; match: Match; word: string; lead?: string }
  | {
      op: StoredOp.Or | StoredOp.And;
      left: StoredExpression;
      right: StoredExpression;
      token?: Token;
      opLead?: string;
    };

export function parseStored(input: ByteReader): StoredExpression {
  const stack: StoredExpression[] = [];
  const strings: string[] = [""];

  input.position = 0;
  while (input.bytesAvailable) {
    const opAndFlags = input.readByte();
    const op: StoredOp = opAndFlags & 0x1f;
    const flags = opAndFlags >>> 5;

    switch (op) {
      default:
        throw new Error(`Unknown store op '${op}'.`);

      case StoredOp.Match: {
        const word = readString();
        const lead = readString();

        switch (flags) {
          default:
            throw new Error(`Unknown flags for ${StoredOp[op]} '0x${flags.toString(16)}'.`);
          case Match.Part:
          case Match.Full:
          case Match.FullCase:
            break;
        }
        const match = flags as Match;
        stack.push({ op, word, match, lead });
        break;
      }

      case StoredOp.Or:
      case StoredOp.And: {
        if (stack.length < 2) {
          throw new Error(`Insufficient operands for ${StoredOp[op]} '${stack.length}'.`);
        }
        const right = stack.pop()!;
        const left = stack.pop()!;

        if (flags) {
          throw new Error(`Unknown flags for ${StoredOp[op]} '0x${flags.toString(16)}'.`);
        }
        const token = input.readByte();
        const opLead = readString();

        stack.push({ op, left, right, token, opLead });
        break;
      }

      case StoredOp.End: {
        if (stack.length < 1) {
          throw new Error(`Insufficient operands for ${StoredOp[op]} '${stack.length}'.`);
        }
        const inner = stack.pop()!;
        const trail = readString();
        stack.push({ op, inner, trail });
        break;
      }

      case StoredOp.Paren: {
        if (flags) {
          throw new Error(`Unknown flags for ${StoredOp[op]} '0x${flags.toString(16)}'.`);
        }
        if (stack.length < 1) {
          throw new Error(`Insufficient operands for ${StoredOp[op]} '${stack.length}'.`);
        }
        const inner = stack.pop()!;
        const openLead = readString();
        const closeLead = readString();
        stack.push({ op, inner, openLead, closeLead });
        break;
      }

      case StoredOp.Raw: {
        if (flags) {
          throw new Error(`Unknown flags for ${StoredOp[op]} '0x${flags.toString(16)}'.`);
        }
        const source = input.readUTF();
        stack.push({ op, source });
        break;
      }
    }
  }

  if (stack.length !== 1) {
    throw new Error(`Invalid final stack depth '${stack.length}'.`);
  }
  return stack[0];

  function readString(): string {
    let lead = input.readByte();
    const isRef = !(lead & 1);
    lead >>>= 1;
    let result: string;
    if (isRef) {
      if (lead >= strings.length) {
        throw new Error(`Invalid string index '${lead}"'.`);
      }
      result = strings[lead];
    } else {
      result = input.readUTFBytes(lead);
      strings.push(result);
    }
    return result;
  }
}

export function storedToSource(input: ByteReader): string {
  return expressionToSource(parseStored(input));
}

/** Return an Expression without the stuff specific to StoredExpression. */
export function stripExpression(expr: StoredExpression): Expression {
  switch (expr.op) {
    case StoredOp.Match:
      return { op: Op.Match, match: expr.match, word: expr.word };

    case StoredOp.Or:
    case StoredOp.And:
      return {
        op: expr.op as number,
        left: stripExpression(expr.left),
        right: stripExpression(expr.right),
      };

    case StoredOp.Paren:
    case StoredOp.End:
      return stripExpression(expr.inner);

    case StoredOp.Raw:
      throw new Error("Expression was not parsable.");
  }
}

export function expressionToSource(expr: Expression | StoredExpression): string {
  switch (expr.op) {
    default:
      throw new Error(`Unknown expr '${JSON.stringify(expr)}'.`);

    case StoredOp.Match: {
      let word = expr.word;

      switch (expr.match) {
        case Match.Full:
          word = '"' + word.replace(/"/g, '""') + '"';
          break;
        case Match.FullCase:
          word = "*" + word.replace(/\*/g, "**") + "*";
          break;
      }

      return combine(expr.lead, word);
    }

    case StoredOp.Or: {
      let opString: undefined | string;
      switch (expr.token) {
        default:
          opString = "OR";
          break;
        case Token.White:
          break;
        case Token.Comma:
          opString = ",";
          break;
      }
      return combine(expressionToSource(expr.left), expr.opLead, opString, expressionToSource(expr.right));
    }

    case StoredOp.And:
      return combine(expressionToSource(expr.left), expr.opLead, "AND", expressionToSource(expr.right));

    case StoredOp.End:
      return combine(expressionToSource(expr.inner), expr.trail);

    case StoredOp.Paren:
      return combine(expr.openLead, "(", expressionToSource(expr.inner), expr.closeLead, ")");

    case StoredOp.Raw:
      return combine(expr.source);
  }

  function combine(...items: Array<undefined | string>) {
    let lastIsWord = false;
    let result = "";
    for (const item of items) {
      if (!item) {
        continue;
      }
      if (lastIsWord && isWord(item.charCodeAt(0))) {
        result += " " + item;
      } else {
        result += item;
      }
      lastIsWord = isWord(item.charCodeAt(item.length - 1));
    }
    return result;
  }
}

export class StoredBuilder implements IParseOutput {
  /* start: op*
     op: bit[5]<op-type> (case op-type: 0 = match-op : 1, 2 = bin-op : 29 = paren-op : 30 = error-op)
     match-op: bit[2]<level> bit[1]<0>
               string<word> string<leadWhite>
     bin-op: bit[3]<0> byte<token> string<leadWhite>
     paren-op: bit[3]<0> string<openLeadWhite> string<closeLeadWhite>)
     error-op: bit[3]<0> string<source>
   */

  public static getErrorResult(input: string): Uint8Array {
    const output = new ByteWriter();
    output.length = 0;
    output.writeByte(StoredOp.Raw);
    output.writeUTF(input);
    return output.getResult();
  }

  private input = "";
  private output: null | ByteWriter = null;
  private strings: null | { [value: string]: number } = null;
  private stringIndex = 0;

  public begin(input: string) {
    this.input = input;
    this.output = new ByteWriter();
    this.strings = {};
  }

  public getResult(): Uint8Array {
    if (!this.output) {
      throw new Error("Not started");
    }
    const result = this.output.getResult();
    this.output = null;
    return result;
  }

  public writeString(value: string) {
    const { strings, output } = this;
    if (!output) {
      throw new Error("Not started");
    }
    if (!strings) {
      throw new Error("Not started");
    }
    if (value in strings) {
      output.writeByte(strings[value] << 1);
    } else {
      output.writeByte(0);
      if (value) {
        strings[value] = ++this.stringIndex;
        const start = output.position;
        output.writeUTFBytes(value);
        output.set(start - 1, ((output.position - start) << 1) | 1);
      }
    }
  }

  public matchExpr(word: string, level: number, lead: number, start: number) {
    if (!this.output) {
      throw new Error("Not started");
    }
    this.output.writeByte(level << 5);
    this.writeString(word);
    this.writeString(this.input.substring(lead, start));
  }

  public binaryExpr(op: number, opToken: number, lead: number, start: number) {
    if (!this.output) {
      throw new Error("Not started");
    }
    this.output.writeByte(op);
    this.output.writeByte(opToken);
    this.writeString(this.input.substring(lead, start));
  }

  public parenExpr(openLead: number, openStart: number, closeLead: number, closeStart: number) {
    if (!this.output) {
      throw new Error("Not started");
    }
    this.output.writeByte(StoredOp.Paren);
    this.writeString(this.input.substring(openLead, openStart));
    this.writeString(this.input.substring(closeLead, closeStart));
  }

  public end(lead: number, index: number) {
    if (!this.output) {
      throw new Error("Not started");
    }
    this.output.writeByte(StoredOp.End);
    this.writeString(this.input.substring(lead, index));
  }
}
